import { useId } from 'react';
import { Seletor, type OpcaoDoSeletor } from '../componentes/Seletor';
import { IconeAgrupar, IconeFiltro, IconeLixeira, IconeOrdenar } from '../componentes/icones';
import {
  AGRUPAMENTOS,
  ARRANJO_PADRAO,
  arranjoNoPadrao,
  ORDENACOES,
  type AgruparPor,
  type ArranjoDeSessoes,
  type OrdenarPor,
} from './arranjoDeSessoes';

/**
 * Um controle da barra: ícone sozinho quando está no padrão, ícone **mais o
 * valor** quando não está.
 *
 * As duas metades desta regra vêm do mesmo relato. O dono pediu "só os ícones"
 * porque no tablet a barra empilhava em três fileiras e comia a altura da
 * lista, que é o conteúdo. Mas ícone mudo o tempo todo criaria outro defeito, e
 * pior: ele veria uma lista incompleta sem nada na tela explicando por quê.
 *
 * Como a barra passa a maior parte da vida no padrão, o caso comum é o
 * compacto; o texto só aparece quando há algo a confessar. O nome acessível
 * nunca depende disso — vem do rótulo de fora, amarrado por `aria-labelledby`,
 * e o `title` repete para quem usa mouse.
 */
function Controle<T extends string>({
  rotulo,
  icone,
  valor,
  padrao,
  opcoes,
  aoEscolher,
}: {
  rotulo: string;
  icone: React.ReactNode;
  valor: T;
  padrao: T;
  opcoes: OpcaoDoSeletor<T>[];
  aoEscolher: (id: T) => void;
}): React.JSX.Element {
  /*
    O id vem do `useId`, e nao do rotulo.

    `aria-labelledby` e uma lista de ids SEPARADA POR ESPACO: montado como
    `rot-Agrupar a lista`, o rotulo virava tres ids inexistentes e o botao
    ficava sem nome nenhum — icone mudo, que e exatamente o defeito que a
    barra so de icones nao pode ter. Foi o ensaio de largura que acusou.
  */
  const idDoRotulo = useId();
  const escolhida = opcoes.find((o) => o.id === valor);
  const noPadrao = valor === padrao;
  return (
    <>
      <span id={idDoRotulo} className="sr-only">
        {rotulo}
      </span>
      <Seletor<T>
        variante="pilula"
        idDoRotulo={idDoRotulo}
        titulo={rotulo}
        rotulo={noPadrao ? '' : (escolhida?.rotulo ?? '')}
        icone={icone}
        valor={valor}
        opcoes={opcoes}
        confirmacao={() => null}
        aoEscolher={(id) => aoEscolher(id)}
      />
    </>
  );
}

/**
 * A barra que arruma a lista de sessões.
 *
 * Mora na mesma fileira do campo de busca, de propósito: numa coluna de 300 px
 * cada fileira a mais é uma conversa a menos visível, e no tablet em paisagem a
 * altura é o recurso escasso. Antes eram três fileiras e a lista terminava
 * espremida no rodapé.
 *
 * O "limpar" só aparece fora do padrão — presente sempre, seria um botão que na
 * maior parte das visitas não faz nada; aparecendo, ele é a resposta à pergunta
 * "por que esta lista está estranha?", no lugar onde ela é feita.
 */
export function BarraDeSessoes({
  arranjo,
  aoMudar,
  projetos,
}: {
  arranjo: ArranjoDeSessoes;
  aoMudar: (mudanca: Partial<ArranjoDeSessoes>) => void;
  projetos: { chave: string; rotulo: string }[];
}): React.JSX.Element {
  const opcoesDeProjeto: OpcaoDoSeletor<string>[] = [
    { id: '', rotulo: 'Todos os projetos' },
    ...projetos.map((p) => ({ id: p.chave, rotulo: p.rotulo })),
  ];

  /*
    `min-w-0`, e nao `shrink-0` — a mesma lição que o grupo de fora já
    aprendeu, e que aqui dentro tinha ficado por fazer.

    Com `shrink-0` esta barra guardava a largura de UMA fileira das próprias
    pílulas, então o `flex-wrap` daqui nunca disparava. Enquanto os três
    controles eram só ícones ninguém notou: a fileira cabia. Ligar o filtro de
    projeto acende a pílula mais larga da barra — a única que carrega nome de
    pasta, de tamanho imprevisível — e aí a conta estourava.

    Medido no telefone de 390 px com o projeto "Tramitacao Inteligente": a
    barra ficava com **442 px** e começava em **x = -60**; somando a ordenação
    fora do padrão, 502 px começando em **-120**, com a pílula de agrupar
    inteira do lado de fora. Para a ESQUERDA porque é para lá que o
    `justify-end` do grupo joga o que não coube — e sem barra de rolagem
    nenhuma, porque quem recorta é o `overflow-hidden` do casco. Silêncio
    completo: o controle não fica apertado, ele deixa de existir.

    Na coluna estreita do desktop era o mesmo, e pior de enxergar: a barra
    começava em x = -3 numa coluna que começa em 160.
  */
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-0.5" data-testid="barra-de-sessoes">
      <Controle<AgruparPor>
        rotulo="Agrupar a lista"
        icone={<IconeAgrupar tamanho={14} />}
        valor={arranjo.agrupar}
        padrao={ARRANJO_PADRAO.agrupar}
        opcoes={AGRUPAMENTOS}
        aoEscolher={(agrupar) => aoMudar({ agrupar })}
      />
      <Controle<OrdenarPor>
        rotulo="Ordenar a lista"
        icone={<IconeOrdenar tamanho={14} />}
        valor={arranjo.ordenar}
        padrao={ARRANJO_PADRAO.ordenar}
        opcoes={ORDENACOES}
        aoEscolher={(ordenar) => aoMudar({ ordenar })}
      />
      {/*
        O filtro de projeto some quando há um projeto só: "Todos os projetos" e
        "Remoto" filtrariam a mesma coisa. Some só no padrão — com filtro ligado
        ele fica, senão sumiria justamente o controle que o desliga.
      */}
      {projetos.length > 1 || arranjo.projeto ? (
        <Controle<string>
          rotulo="Filtrar por projeto"
          icone={<IconeFiltro tamanho={14} />}
          valor={arranjo.projeto}
          padrao={ARRANJO_PADRAO.projeto}
          opcoes={opcoesDeProjeto}
          aoEscolher={(projeto) => aoMudar({ projeto })}
        />
      ) : null}
      {/*
        Lixeira, e não a palavra "limpar".

        Escrito, ele era o mais largo da fileira e o último da direita: na
        coluna estreita terminava 84 px fora da borda, cortado. Como ícone ele
        cabe — e some do caminho quando não há nada a limpar, que é a maior
        parte do tempo.

        O vermelho é `text-red-400`, e não o `bg-red-600` do botão perigoso do
        `Modal`. A casa já separa os dois: sólido é ação sem volta (encerrar,
        remover máquina), vermelho de texto é aviso. Limpar filtro se refaz em
        dois cliques — pintá-lo como o botão de encerrar ensinaria o olho a não
        levar o vermelho a sério no dia em que ele significar destruição.

        `aria-label`, e não `aria-labelledby`: é texto direto, sem a armadilha
        da lista de ids separada por espaço que já deixou uma pílula desta
        barra sem nome nenhum.
      */}
      {arranjoNoPadrao(arranjo) ? null : (
        <button
          onClick={() => aoMudar(ARRANJO_PADRAO)}
          title="Limpar: voltar ao agrupamento e à ordem de sempre"
          aria-label="Limpar o arranjo da lista"
          className="flex shrink-0 items-center justify-center rounded-md px-1.5 py-1 text-red-400 transition hover:bg-red-950/40 hover:text-red-300 pointer-coarse:h-11 pointer-coarse:w-11"
        >
          <IconeLixeira tamanho={14} />
        </button>
      )}
    </div>
  );
}
